import { ApplicationState } from '@app/store'
import { Store } from 'redux'
import { buffers, END, eventChannel } from 'redux-saga'

let storeInstance: Store<ApplicationState>

export enum RequestMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'Delete',
}

interface IRequestHeaders {
  [key: string]: string
}

export interface IRequestOptions {
  method?: RequestMethod
  credentials?: string
  body?: any
  headers?: IRequestHeaders
  noDefaultHeaders?: boolean,
}

const DEFAULT_HEADERS: IRequestHeaders = {
  "accept": 'application/json',
  'content-type': 'application/json',
}

export function createUploadFileChannel(endpoint: string, formData: FormData) {
  return eventChannel((emitter) => {
    const xhr = new XMLHttpRequest()

    let lastProgress: number | undefined

    const onProgress = (e: ProgressEvent) => {
      if (e.lengthComputable) {
        const progress = e.loaded / e.total
        if (
          lastProgress === undefined ||
          progress > lastProgress + 0.01 ||
          progress === 1
        ) {
          lastProgress = progress
          emitter({ progress })
        }
      }
    }

    const onFailure = (e: any) => {
      emitter({ err: new Error('Upload failed') })
      emitter(END)
    }

    xhr.open('POST', endpoint, true)

    xhr.withCredentials = true
    xhr.upload.addEventListener('progress', onProgress, false)
    xhr.upload.addEventListener('error', onFailure)
    xhr.upload.addEventListener('abort', onFailure)
    xhr.onreadystatechange = () => {
      const { readyState, response, status } = xhr
      if (readyState === 4) {
        if (status === 200) {
          if (response) {
            const jsonResponse = JSON.parse(response)
            if (jsonResponse.success) {
              emitter({ success: true, response: jsonResponse })
            } else {
              emitter({ err: new Error(jsonResponse.message) })
            }
            emitter(END)
          }
        } else {
          onFailure(null)
        }
      }
    }

    xhr.setRequestHeader('Authorization', `Bearer ${getAccessToken()}`)
    xhr.send(formData)

    return () => {
      xhr.upload.removeEventListener('progress', onProgress, false)
      xhr.upload.removeEventListener('error', onFailure)
      xhr.upload.removeEventListener('abort', onFailure)
      xhr.onreadystatechange = null
      xhr.abort()
    }
  }, buffers.sliding(2))
}

export function makeAuthenticatedRequest(
  url: string,
  options?: IRequestOptions,
): Promise<any> {
  try {
    const opts = prepareOptions(options)

    if (opts.headers) {
      opts.headers.authorization = `Bearer ${getAccessToken()}`
    } else {
      opts.headers = { authorization: `Bearer ${getAccessToken()}` }
    }

    return makeRequestInternal(url, opts)
  } catch (err) {
    return Promise.reject(err)
  }
}

export function makeRequest(
  url: string,
  options?: IRequestOptions,
): Promise<any> {
  try {
    const opts = prepareOptions(options)

    return makeRequestInternal(url, opts)
  } catch (err) {
    return Promise.reject(err)
  }
}

export function prepareRequest(store: Store<ApplicationState>): void {
  storeInstance = store
}

function prepareOptions(options: IRequestOptions = {}): IRequestOptions {
  const { method, credentials, headers, noDefaultHeaders, ...rest } = options

  return {
    credentials: credentials || 'same-origin',
    headers: noDefaultHeaders ? headers : Object.assign({}, DEFAULT_HEADERS, headers),
    method: method || RequestMethod.GET,
    ...rest,
  }
}

function getAccessToken(): string | undefined {
  if (!storeInstance) {
    return undefined
  }

  const state = storeInstance.getState()
  return state.account.accessToken
}

function makeRequestInternal(
  url: string,
  options: IRequestOptions,
): Promise<any> {
  return fetch(url, options as RequestInit).then(parseResponse)
}

function parseResponse(response: Response): Promise<any> {
  if (response.status >= 500) {
    throw new Error(
      `Received response with server error status ${response.status}`,
    )
  }

  const contentType = response.headers.get('content-type')
  if (contentType && contentType.indexOf('application/json') !== -1) {
    return response.json()
  } else {
    return Promise.resolve(response.status === 200)
  }
}
